/***
 *
 *   SIGN IN OTP (2FA)
 *   Phase 9 — segmented code field chrome (hand-rolled). Keeps live 2FA API
 *   (/api/auth/otp + jwt) — not the reference email-verify flow.
 *
 **********/

import React, { useContext, useEffect, useRef, useState } from 'react';
import { Animate, Button, useNavigate, useLocation, cn } from 'components/lib';
import { AuthPage } from 'components/auth/authPage';
import { AuthContext } from 'app/auth';
import { useTranslate } from 'app/translations';
import { useApiContext } from 'app/api';
import Style from './otp.tailwind';

const LENGTH = 6;

export function SigninOTP(props) {
    const { _t } = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const context = useContext(AuthContext);
    const { callApi } = useApiContext();

    const token = location?.search?.includes('token=')
        ? location.search.substring(location.search.indexOf('token=') + 6)
        : '';

    const [digits, setDigits] = useState(Array(LENGTH).fill(''));
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const refs = useRef([]);

    useEffect(() => {
        if (!token) navigate('/signin');
    }, [token, navigate]);

    const code = digits.join('');
    const ready = code.length === LENGTH;

    function setDigitAt(index, value) {
        const next = [...digits];
        next[index] = value;
        setDigits(next);
        return next;
    }

    function onChange(index, raw) {
        const digit = raw.replace(/\D/g, '').slice(-1);
        const next = setDigitAt(index, digit);
        setError('');
        if (digit && index < LENGTH - 1) {
            refs.current[index + 1]?.focus();
        }
        if (next.join('').length === LENGTH) {
            void submit(next.join(''));
        }
    }

    function onKeyDown(index, e) {
        if (e.key === 'Backspace' && !digits[index] && index > 0) {
            refs.current[index - 1]?.focus();
        }
    }

    function onPaste(e) {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, LENGTH);
        if (!pasted) return;
        const next = Array(LENGTH)
            .fill('')
            .map((_, i) => pasted[i] || '');
        setDigits(next);
        const focusAt = Math.min(pasted.length, LENGTH - 1);
        refs.current[focusAt]?.focus();
        if (pasted.length === LENGTH) void submit(pasted);
    }

    async function submit(value) {
        const payload = value || code;
        if (payload.length !== LENGTH || submitting) return;
        setSubmitting(true);
        setError('');
        try {
            const res = await callApi({
                method: 'post',
                url: '/api/auth/otp',
                data: { code: payload, jwt: token },
            });
            if (res.status >= 400 || res.message) {
                setError(res.message || _t('error.tryAgain'));
                setDigits(Array(LENGTH).fill(''));
                refs.current[0]?.focus();
                return;
            }
            context.signin(res);
        } catch (err) {
            setError(err?.message || _t('error.tryAgain'));
            setDigits(Array(LENGTH).fill(''));
            refs.current[0]?.focus();
        } finally {
            setSubmitting(false);
        }
    }

    if (!token) return null;

    return (
        <Animate type="pop">
            <AuthPage title={_t('code.enter')} description={_t('code.enterDesc')}>
                <div className={Style.row} onPaste={onPaste}>
                    {digits.map((digit, index) => (
                        <input
                            key={index}
                            ref={(el) => {
                                refs.current[index] = el;
                            }}
                            type="text"
                            inputMode="numeric"
                            autoComplete={index === 0 ? 'one-time-code' : 'off'}
                            maxLength={1}
                            value={digit}
                            className={cn(Style.cell, error && Style.cellError)}
                            onChange={(e) => onChange(index, e.target.value)}
                            onKeyDown={(e) => onKeyDown(index, e)}
                            aria-label={`${_t('code.label')} ${index + 1}`}
                        />
                    ))}
                </div>

                {error && <p className={Style.error}>{error}</p>}

                <Button
                    variant="accent"
                    fullWidth
                    text={_t('signin.complete')}
                    disabled={!ready || submitting}
                    isLoading={submitting}
                    action={() => void submit()}
                />

                <p className={Style.hint}>{_t('code.lostAccess')}</p>
            </AuthPage>
        </Animate>
    );
}
